import { useEffect } from 'react';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { CircleAlert, RotateCcw } from 'lucide-react';
import { Navigate, Outlet, useLocation } from 'react-router';

import { EmptyState } from '@components/patterns';
import { Button } from '@components/primitives';

import { CommandProvider } from '@components/features/CommandMenu';
import { GlobalCommands } from '@components/features/CommandMenu/static/GlobalCommands';
import { UserCommands } from '@components/features/CommandMenu/static/UserCommands';
import Layout from '@components/features/Layout';
import { PlainProvider } from '@components/features/Plain';
import { isRoleMatch } from '@components/features/Restricted';
import { useAuth0 } from '@hooks/useAuth0';
import { useCurrentTeam, useUserQuery } from '@queries/user';
import NotFound from '@routes/NotFound';

import { getLoginRedirect, setLoginRedirect } from './loginRedirectStorage';

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const userQuery = useUserQuery({ enabled: isAuthenticated });

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (isAuthenticated) {
      // clear the stored login redirect if the user is authenticated
      setLoginRedirect(null);
      return;
    }

    setLoginRedirect(location);
    void loginWithRedirect();
  }, [isLoading, isAuthenticated, loginWithRedirect, location]);

  if (!isAuthenticated) {
    return <Layout.Loader />;
  }

  if (userQuery.isError && !userQuery.data) {
    return (
      <EmptyState variant="failure">
        <EmptyState.Icon>
          <CircleAlert />
        </EmptyState.Icon>
        <EmptyState.Title>We couldn’t load your account</EmptyState.Title>
        <EmptyState.Text>
          Something went wrong while loading your account. Reload the page to
          try again.
        </EmptyState.Text>
        <Button onClick={() => window.location.reload()}>
          <RotateCcw />
          Reload page
        </Button>
      </EmptyState>
    );
  }

  return (
    <PlainProvider>
      <CommandProvider>
        <GlobalCommands />
        {children}
      </CommandProvider>
    </PlainProvider>
  );
}

export function RequireAuthOutlet() {
  return (
    <RequireAuth>
      <UserCommands />
      <Outlet />
    </RequireAuth>
  );
}

export function RequireSuperuser({
  children,
  fallback = <Navigate to="/" replace />,
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const { data } = useUserQuery({ suspense: true });

  const isSuperUser = !!data && isRoleMatch('superuser', data);

  if (!isSuperUser) {
    return fallback;
  }

  return children;
}

export function RequireSupport({
  children,
  fallback = <Navigate to="/" replace />,
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const { data } = useUserQuery({ suspense: true });

  const isInternal = !!data && isRoleMatch('internal', data);

  if (!isInternal) {
    return fallback;
  }

  return children;
}

// The opposite to <RequireAuth />. This will redirect to the root path if the user is
// logged in.
function RequireNotAuthed({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth0();

  if (isLoading) {
    return <Layout.Loader />;
  }

  if (isAuthenticated) {
    const to = getLoginRedirect() || '/';
    return <Navigate to={to} replace />;
  }

  return children;
}

export function RequireNotAuthedOutlet() {
  return (
    <RequireNotAuthed>
      <Outlet />
    </RequireNotAuthed>
  );
}

// The ASV Scans sign-up, until the `free-asv-scans` flag turns it on. The LD
// client is only set once its flags have loaded (or failed to), so this waits
// for it rather than turning everyone away on the empty flags it starts with.
export function RequireFreeAsvScansOutlet() {
  const ldClient = useLDClient();
  const { freeAsvScans } = useFlags();

  if (!ldClient) return <Layout.Loader />;
  if (!freeAsvScans) return <NotFound />;

  return <Outlet />;
}

export function RequireTeamAccess({ children }: { children: React.ReactNode }) {
  const { loading, team } = useCurrentTeam();

  if (loading) return <Layout.Loader />;
  if (!team) return <NotFound />;

  return children;
}
